import React from 'react';
import { AlertCircle, Link2, Shield, FileText, CheckCircle } from 'lucide-react';
import Icon from '../../../components/AppIcon';


const TechnicalAuditPanel = ({ issues, siteHealth }) => {
  const getSeverityIcon = (severity) => {
    if (severity === 'high') return <AlertCircle className="text-red-600" size={20} />;
    if (severity === 'medium') return <AlertCircle className="text-orange-600" size={20} />;
    return <AlertCircle className="text-blue-600" size={20} />;
  };

  const getSeverityColor = (severity) => {
    if (severity === 'high') return 'bg-red-50 border-red-200';
    if (severity === 'medium') return 'bg-orange-50 border-orange-200';
    return 'bg-blue-50 border-blue-200';
  };

  const getPriorityBadge = (priority) => {
    const colors = {
      urgent: 'bg-red-100 text-red-800 border-red-300',
      high: 'bg-orange-100 text-orange-800 border-orange-300',
      medium: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      low: 'bg-blue-100 text-blue-800 border-blue-300'
    };
    return colors?.[priority] || colors?.medium;
  };

  const auditCategories = [
    {
      title: 'Indexation & Crawl',
      icon: FileText,
      metrics: [
        { label: 'Pages indexées', value: siteHealth?.indexedPages, status: 'good' },
        { label: 'Erreurs de crawl', value: siteHealth?.crawlErrors, status: siteHealth?.crawlErrors > 0 ? 'error' : 'good' }
      ]
    },
    {
      title: 'Structure & Liens',
      icon: Link2,
      metrics: [
        { label: 'Liens cassés', value: siteHealth?.brokenLinks, status: siteHealth?.brokenLinks > 0 ? 'error' : 'good' },
        { label: 'Contenu dupliqué', value: siteHealth?.duplicateContent, status: siteHealth?.duplicateContent > 0 ? 'warning' : 'good' }
      ]
    },
    {
      title: 'Sécurité',
      icon: Shield,
      metrics: [
        { label: 'HTTPS', value: 'Actif', status: 'good' },
        { label: 'Certificat SSL', value: 'Valide', status: 'good' }
      ]
    }
  ];

  return (
    <div className="space-y-6">
      {/* Audit Categories Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {auditCategories?.map((category, index) => {
          const Icon = category?.icon;
          return (
            <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-green-100 text-green-600 rounded-lg">
                  <Icon size={20} />
                </div>
                <h3 className="font-semibold text-gray-900">{category?.title}</h3>
              </div>
              <div className="space-y-3">
                {category?.metrics?.map((metric, idx) => (
                  <div key={idx} className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">{metric?.label}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-900">{metric?.value}</span>
                      {metric?.status === 'good' && <CheckCircle className="text-green-600" size={16} />}
                      {metric?.status === 'error' && <AlertCircle className="text-red-600" size={16} />}
                      {metric?.status === 'warning' && <AlertCircle className="text-orange-600" size={16} />}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Issues List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center gap-2">
          <AlertCircle className="text-orange-600" size={24} />
          Problèmes Détectés
        </h2>

        <div className="space-y-4">
          {issues?.map((issue, index) => (
            <div key={index} className={`border rounded-lg p-5 ${getSeverityColor(issue?.severity)}`}>
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 mt-1">
                  {getSeverityIcon(issue?.severity)}
                </div>
                <div className="flex-1">
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-semibold text-gray-900">{issue?.issue}</h4>
                    <span className={`px-3 py-1 text-xs font-medium rounded-full border ${getPriorityBadge(issue?.priority)}`}>
                      {issue?.priority?.toUpperCase()}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700 mb-3">
                    {issue?.affected} {issue?.affected > 1 ? 'pages affectées' : 'page affectée'}
                  </p>
                  <div className="bg-white bg-opacity-50 rounded-lg p-3">
                    <p className="text-xs font-medium text-gray-700 mb-1">Recommandation:</p>
                    <p className="text-sm text-gray-600">
                      {issue?.type === 'error' && 'Corriger immédiatement ces erreurs pour éviter une perte de trafic.'}
                      {issue?.type === 'warning' && 'Planifier une correction dans les prochains jours.'}
                      {issue?.type === 'info' && 'Amélioration recommandée pour optimiser le référencement.'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {issues?.length === 0 && (
          <div className="text-center py-8">
            <CheckCircle className="mx-auto text-green-600 mb-3" size={48} />
            <p className="text-gray-600 font-medium">Aucun problème détecté</p>
            <p className="text-sm text-gray-500 mt-1">Votre site est techniquement optimisé</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TechnicalAuditPanel;